import { useState, useEffect } from 'react';
import { assetsAPI, vehiclesAPI, tenantUsersAPI, suppliersAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { FormLabel } from '../../../components/ui/FormLabel';
import {
  ASSET_CATEGORIES,
  ASSET_STATUSES,
  LOCATION_TYPES,
} from '../../../lib/assetConstants';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
    {children}
  </h3>
);

const emptyForm = () => ({
  name: '',
  category: 'other',
  manufacturer: '',
  model: '',
  serialNumber: '',
  description: '',
  status: 'in_service',
  locationType: '',
  locationNotes: '',
  assignedUserId: '',
  assignedVehicleId: '',
  purchaseDate: '',
  purchaseSupplierId: '',
  purchaseSupplierName: '',
  purchaseReceiptRef: '',
  purchaseNetAmount: '',
  purchaseVatAmount: '',
  purchaseGrossAmount: '',
  warrantyStartDate: '',
  warrantyExpiryDate: '',
  warrantyProvider: '',
  warrantyNotes: '',
  currentValue: '',
  replacementValue: '',
  lastCalibrationDate: '',
  calibrationDueDate: '',
  serviceDueDate: '',
  serviceIntervalMonths: '',
  ownedBeforeSparxmate: false,
  eligibleForPreRegistrationVatReclaim: false,
  notes: '',
});

const assetToForm = (asset) => ({
  name: asset?.name || '',
  category: asset?.category || 'other',
  manufacturer: asset?.manufacturer || '',
  model: asset?.model || '',
  serialNumber: asset?.serialNumber || '',
  description: asset?.description || '',
  status: asset?.status || 'in_service',
  locationType: asset?.locationType || '',
  locationNotes: asset?.locationNotes || '',
  assignedUserId: asset?.assignedUserId || '',
  assignedVehicleId: asset?.assignedVehicleId || '',
  purchaseDate: asset?.purchaseDate?.slice?.(0, 10) || asset?.purchaseDate || '',
  purchaseSupplierId: asset?.purchaseSupplierId || '',
  purchaseSupplierName: asset?.purchaseSupplierName || '',
  purchaseReceiptRef: asset?.purchaseReceiptRef || '',
  purchaseNetAmount: asset?.purchaseNetAmount ?? '',
  purchaseVatAmount: asset?.purchaseVatAmount ?? '',
  purchaseGrossAmount: asset?.purchaseGrossAmount ?? '',
  warrantyStartDate: asset?.warrantyStartDate?.slice?.(0, 10) || asset?.warrantyStartDate || '',
  warrantyExpiryDate: asset?.warrantyExpiryDate?.slice?.(0, 10) || asset?.warrantyExpiryDate || '',
  warrantyProvider: asset?.warrantyProvider || '',
  warrantyNotes: asset?.warrantyNotes || '',
  currentValue: asset?.currentValue ?? '',
  replacementValue: asset?.replacementValue ?? '',
  lastCalibrationDate: asset?.lastCalibrationDate?.slice?.(0, 10) || asset?.lastCalibrationDate || '',
  calibrationDueDate: asset?.calibrationDueDate?.slice?.(0, 10) || asset?.calibrationDueDate || '',
  serviceDueDate: asset?.serviceDueDate?.slice?.(0, 10) || asset?.serviceDueDate || '',
  serviceIntervalMonths: asset?.serviceIntervalMonths ?? '',
  ownedBeforeSparxmate: Boolean(asset?.ownedBeforeSparxmate),
  eligibleForPreRegistrationVatReclaim: Boolean(asset?.eligibleForPreRegistrationVatReclaim),
  notes: asset?.notes || '',
});

const parseAmount = (val) => {
  if (val === '' || val == null) return null;
  const n = parseFloat(val);
  return Number.isNaN(n) ? null : n;
};

const parseIntOrNull = (val) => {
  if (val === '' || val == null) return null;
  const n = parseInt(val, 10);
  return Number.isNaN(n) ? null : n;
};

const AssetForm = ({ asset, onClose, onSuccess }) => {
  const isEdit = !!asset?.id;

  const [formData, setFormData] = useState(() => (asset ? assetToForm(asset) : emptyForm()));
  const [nextRef, setNextRef] = useState('');
  const [tenantUsers, setTenantUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (asset) setFormData(assetToForm(asset));
  }, [asset]);

  useEffect(() => {
    const load = async () => {
      try {
        const [usersRes, vehiclesRes, suppliersRes] = await Promise.all([
          tenantUsersAPI.list(),
          vehiclesAPI.getAll({ limit: 100, is_active: true }),
          suppliersAPI.getAll({ limit: 200, is_active: true }),
        ]);
        const users = usersRes?.users || usersRes || [];
        setTenantUsers(Array.isArray(users) ? users : []);
        setVehicles(vehiclesRes?.vehicles || []);
        setSuppliers(suppliersRes?.suppliers || []);
        if (!isEdit) {
          const refRes = await assetsAPI.getNextRef();
          setNextRef(refRes?.assetRef || '');
        }
      } catch (err) {
        console.error('Failed to load form lookups:', err);
      }
    };
    load();
  }, [isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildPayload = () => ({
    name: formData.name.trim(),
    category: formData.category,
    manufacturer: formData.manufacturer?.trim() || null,
    model: formData.model?.trim() || null,
    serialNumber: formData.serialNumber?.trim() || null,
    description: formData.description?.trim() || null,
    status: formData.status,
    locationType: formData.locationType || null,
    locationNotes: formData.locationNotes?.trim() || null,
    assignedUserId: formData.assignedUserId || null,
    assignedVehicleId: formData.assignedVehicleId || null,
    purchaseDate: formData.purchaseDate || null,
    purchaseSupplierId: formData.purchaseSupplierId || null,
    purchaseSupplierName: formData.purchaseSupplierName?.trim() || null,
    purchaseReceiptRef: formData.purchaseReceiptRef?.trim() || null,
    purchaseNetAmount: parseAmount(formData.purchaseNetAmount),
    purchaseVatAmount: parseAmount(formData.purchaseVatAmount),
    purchaseGrossAmount: parseAmount(formData.purchaseGrossAmount),
    warrantyStartDate: formData.warrantyStartDate || null,
    warrantyExpiryDate: formData.warrantyExpiryDate || null,
    warrantyProvider: formData.warrantyProvider?.trim() || null,
    warrantyNotes: formData.warrantyNotes?.trim() || null,
    currentValue: parseAmount(formData.currentValue),
    replacementValue: parseAmount(formData.replacementValue),
    lastCalibrationDate: formData.lastCalibrationDate || null,
    calibrationDueDate: formData.calibrationDueDate || null,
    serviceDueDate: formData.serviceDueDate || null,
    serviceIntervalMonths: parseIntOrNull(formData.serviceIntervalMonths),
    ownedBeforeSparxmate: formData.ownedBeforeSparxmate,
    eligibleForPreRegistrationVatReclaim: formData.eligibleForPreRegistrationVatReclaim,
    notes: formData.notes?.trim() || null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = buildPayload();
      if (isEdit) {
        await assetsAPI.update(asset.id, payload);
      } else {
        await assetsAPI.create(payload);
      }
      onSuccess?.();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          err.message ||
          'Failed to save asset'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {!isEdit && nextRef && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Next reference: <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{nextRef}</span>
        </p>
      )}

      <div>
        <SectionTitle>Identity</SectionTitle>
        <div className="space-y-4">
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">Name</FormLabel>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel required className="text-gray-700 dark:text-gray-300">Category</FormLabel>
              <select name="category" value={formData.category} onChange={handleChange} className={selectClass} required>
                {ASSET_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Status</FormLabel>
              <select name="status" value={formData.status} onChange={handleChange} className={selectClass}>
                {ASSET_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Manufacturer</FormLabel>
              <Input name="manufacturer" value={formData.manufacturer} onChange={handleChange}
                className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Model</FormLabel>
              <Input name="model" value={formData.model} onChange={handleChange}
                className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Serial number</FormLabel>
              <Input name="serialNumber" value={formData.serialNumber} onChange={handleChange}
                className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Description</FormLabel>
            <Textarea name="description" value={formData.description} onChange={handleChange} rows={2}
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Location & assignment</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Location type</FormLabel>
            <select name="locationType" value={formData.locationType} onChange={handleChange} className={selectClass}>
              <option value="">— Not set —</option>
              {LOCATION_TYPES.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Location notes</FormLabel>
            <Input name="locationNotes" value={formData.locationNotes} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Assigned to</FormLabel>
            <select name="assignedUserId" value={formData.assignedUserId} onChange={handleChange} className={selectClass}>
              <option value="">— Unassigned —</option>
              {tenantUsers
                .filter((u) => ['tenant_admin', 'tenant_user'].includes(u.role))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {[u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ').trim() || u.email}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Assigned vehicle</FormLabel>
            <select name="assignedVehicleId" value={formData.assignedVehicleId} onChange={handleChange} className={selectClass}>
              <option value="">— None —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.reg_number || v.id}{v.make || v.model ? ` — ${[v.make, v.model].filter(Boolean).join(' ')}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Purchase</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Purchase date</FormLabel>
            <Input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Receipt reference</FormLabel>
            <Input name="purchaseReceiptRef" value={formData.purchaseReceiptRef} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Supplier (linked)</FormLabel>
            <select name="purchaseSupplierId" value={formData.purchaseSupplierId} onChange={handleChange} className={selectClass}>
              <option value="">— None —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.supplier_name}</option>
              ))}
            </select>
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Supplier name (free text)</FormLabel>
            <Input name="purchaseSupplierName" value={formData.purchaseSupplierName} onChange={handleChange}
              placeholder="For historic or non-linked suppliers"
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Net (£)</FormLabel>
            <Input type="number" step="0.01" min="0" name="purchaseNetAmount" value={formData.purchaseNetAmount} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">VAT (£)</FormLabel>
            <Input type="number" step="0.01" min="0" name="purchaseVatAmount" value={formData.purchaseVatAmount} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Gross (£)</FormLabel>
            <Input type="number" step="0.01" min="0" name="purchaseGrossAmount" value={formData.purchaseGrossAmount} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Warranty</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Warranty start</FormLabel>
            <Input type="date" name="warrantyStartDate" value={formData.warrantyStartDate} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Warranty expiry</FormLabel>
            <Input type="date" name="warrantyExpiryDate" value={formData.warrantyExpiryDate} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Provider</FormLabel>
            <Input name="warrantyProvider" value={formData.warrantyProvider} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Warranty notes</FormLabel>
            <Input name="warrantyNotes" value={formData.warrantyNotes} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Valuation & service</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Current value (£)</FormLabel>
            <Input type="number" step="0.01" min="0" name="currentValue" value={formData.currentValue} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Replacement value (£)</FormLabel>
            <Input type="number" step="0.01" min="0" name="replacementValue" value={formData.replacementValue} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Last calibration</FormLabel>
            <Input type="date" name="lastCalibrationDate" value={formData.lastCalibrationDate} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Calibration due</FormLabel>
            <Input type="date" name="calibrationDueDate" value={formData.calibrationDueDate} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Service due</FormLabel>
            <Input type="date" name="serviceDueDate" value={formData.serviceDueDate} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Service interval (months)</FormLabel>
            <Input type="number" min="1" name="serviceIntervalMonths" value={formData.serviceIntervalMonths} onChange={handleChange}
              className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Historic VAT flags</SectionTitle>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              name="ownedBeforeSparxmate"
              checked={formData.ownedBeforeSparxmate}
              onChange={handleChange}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Owned before SparxMate
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              name="eligibleForPreRegistrationVatReclaim"
              checked={formData.eligibleForPreRegistrationVatReclaim}
              onChange={handleChange}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Eligible for pre-registration VAT reclaim (advisory)
          </label>
        </div>
      </div>

      <div>
        <FormLabel className="text-gray-700 dark:text-gray-300">Notes</FormLabel>
        <Textarea name="notes" value={formData.notes} onChange={handleChange} rows={3}
          className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onClose && (
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Saving…' : isEdit ? 'Update asset' : 'Create asset'}
        </Button>
      </div>
    </form>
  );
};

export default AssetForm;
